import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import {
  Button,
  ButtonSize,
  ButtonColor,
  TextInput,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';
import uid from 'uid';

import { LoanTaxInfoPageValidation, CustomerLoanTaxInfo } from 'src/front/api/apiInstance';
import { ReferenceDataType } from 'src/front/api/generated';

import usePRM from 'src/front/common/hooks/usePRM';

import {
  formatNumeric,
  dictionaryLineFormat,
  optionFormat,
} from 'src/front/common/utils/formUtils';
import { TaxFormData } from 'src/front/pages/applications/card-entry/tax-information/TaxModal';
import {
  Container,
  Form,
  FormRow,
  WarningText,
  TableWrapper,
  Subtitle,
  FormElement,
  Separator,
  Title,
} from 'src/front/components/FormStyles';
import { TaxNumberSchema } from 'src/front/common/validations/loanApplication';
import { useCustomerSearchState } from 'src/front/contexts/LoanApplicationContext';

const taxTableHeaders = [
  'cardEntry_taxInfo_taxResidenceCountry_label',
  'cardEntry_taxInfo_iinnProvided_label',
  'cardEntry_taxInfo_iinn_label',
];

interface FormData {
  taxNumber: string;
}

const defaultValues = {
  taxNumber: '',
};

interface Props {
  goToNextStep: (customerId?: string) => void;
}

type Tax = TaxFormData;

const TaxInformation: FC<Props> = ({ goToNextStep }) => {
  const { t } = useTranslation();

  const [warning, setWarning] = useState<string>();
  const [taxes, setTaxes] = useState<Tax[]>([]);

  const [loadingTable, setLoadingTable] = useState<boolean>(true);
  const { customerId } = useCustomerSearchState();
  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const yesNoComboItems = usePRM(ReferenceDataType.ISPROVIDED);

  const { handleSubmit, errors, setValue, control, formState } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(TaxNumberSchema),
  });

  const loadTaxInformation = useCallback(async () => {
    const { data } = await LoanTaxInfoPageValidation.getLoanTaxInfoPageData();

    if (data.result === 'OK' && data.pageData) {
      const { taxResidencyList, taxNumber } = data.pageData;

      const taxList =
        taxResidencyList?.map((taxResidency) => ({
          taxResidenceCountry: optionFormat(taxResidency.taxResidenceCountry),
          hasTaxNumberProvided: optionFormat(taxResidency.hasTaxNumberProvided),
          foreignTaxNumberId: taxResidency.isNew ? uid() : taxResidency.foreignTaxNumberId,
          differentNumber: taxResidency.differentNumber || false,
          foreignTaxNumber: taxResidency.foreignTaxNumber || '',
          isNew: taxResidency.isNew,
        })) || [];
      setValue('taxNumber', taxNumber || '');
      setTaxes(taxList);
    } else {
      const { data: response } = await CustomerLoanTaxInfo.getCustomerLoanTaxInfo({ customerId });

      if (response.result === 'OK') {
        const [noOption, yesOption] = yesNoComboItems;

        const taxList =
          response.taxResidencyList?.map((tax) => ({
            taxResidenceCountry:
              countries.find((country) => country.value === tax.taxResidenceCountryCode) || null,
            hasTaxNumberProvided: tax.hasTaxNumberProvided ? yesOption : noOption,
            differentNumber: tax.differentNumber || false,
            foreignTaxNumber: tax.foreignTaxNumber || '',
            foreignTaxNumberId: tax.foreignTaxNumberId,
            isNew: tax.isNew,
          })) || [];

        setValue('taxNumber', response.taxNumber || '');
        setTaxes(taxList);
      }
    }
    setLoadingTable(false);
  }, [countries, customerId, setValue, yesNoComboItems]);

  useEffect(() => {
    if (countries.length && yesNoComboItems.length) {
      loadTaxInformation();
    }
  }, [customerId, countries.length, loadTaxInformation, yesNoComboItems.length]);

  const onSubmit = async (form: FormData): Promise<void> => {
    const hasTaxResidency = taxes.length > 0;
    if (hasTaxResidency) {
      warning && setWarning(undefined);

      const { data } = await LoanTaxInfoPageValidation.validateLoanTaxInfoPageData({
        taxNumber: form.taxNumber,
        taxResidencyList: taxes.map((tax) => ({
          taxResidenceCountry: dictionaryLineFormat(tax.taxResidenceCountry),
          hasTaxNumberProvided: dictionaryLineFormat(tax.hasTaxNumberProvided),
          foreignTaxNumber: tax.foreignTaxNumber,
          differentNumber: tax.differentNumber,
          isNew: tax.isNew || false,
          ...(tax.isNew ? {} : { foreignTaxNumberId: tax.foreignTaxNumberId }),
        })),
      });

      if (data.result === 'OK') {
        // goToNextStep();
      }
    } else {
      setWarning('cardEntry_taxInfo_noTaxResidencyInfo_errorMessage');
    }
  };

  const taxTableData: TableRow[] = taxes.map(
    (tax: Tax): TableRow => ({
      id: tax.foreignTaxNumberId || '',
      columns: [
        tax.taxResidenceCountry?.label || '',
        tax.hasTaxNumberProvided?.label || '',
        tax.foreignTaxNumber,
      ],
    })
  );

  return (
    <Container>
      <Title>
        <Trans i18nKey="cardEntry_taxInfo_taxInfo_title" />
      </Title>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="taxNumber"
              render={({ onChange, value }) => (
                <TextInput
                  errorMessage={t(errors?.taxNumber?.message as string)}
                  formatter={formatNumeric}
                  label={t('loanApp_taxInfo_inn_label')}
                  maxLength={12}
                  name="taxNumber"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <Subtitle>
          <Trans i18nKey="loanApp_taxInfo_taxResidency_subtitle" />
        </Subtitle>
        <TableWrapper>
          <Table
            emptyTableText={t('cardEntry_taxInfo_noTaxResidencyTable_errorMessage')}
            headers={taxTableHeaders.map((head) => t(head))}
            items={taxTableData}
            loading={loadingTable}
            pageNumber={1}
            widths={['40', '30', '30']}
          />
        </TableWrapper>
        <WarningText>
          <Trans i18nKey={warning} />
        </WarningText>
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_continue_button')}
            type="submit"
          />
        </FormRow>
      </Form>
    </Container>
  );
};

export default TaxInformation;
